import type { Rule } from "antd/lib/form";
import type { MouseEvent } from "react";

import countryToCurrency from "country-to-currency";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import kebabCase from "lodash/kebabCase";
import { defaultCountries, parseCountry } from "react-international-phone";

import type {
  AddressFormatType,
  GraphQLErrorDetails,
  NameParts,
  PreservationMode,
} from "./types";

import {
  countriesList,
  DEFAULT_CURRENCY,
  NO_DATA,
  PRESERVATION_MODE,
} from "../constants/constants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

export const getOffsetMinutes = (timeZone: string, now: Date): number => {
  const local = dayjs(now).tz(timeZone);
  return local.utcOffset();
};

export const getCurrencySymbol = (currencyCode: string) => {
  const parts = new Intl.NumberFormat("en", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(1);

  return parts.find(part => part.type === "currency")?.value || currencyCode;
};

export const formatCurrency = ({
  currency,
  value = 0,
  maxFractionDigits = 2,
  showCurrency = true,
  currencyPosition = "start",
  forceDecimals = false,
  isMinorUnit = true,
  useCurrencySymbol = false,
  showCurrencyCodeWithSymbol = false,
  positivePrefix = "",
}: {
  currency?: string | null;
  value?: number | null;
  maxFractionDigits?: number;
  showCurrency?: boolean;
  currencyPosition?: "start" | "end";
  forceDecimals?: boolean;
  isMinorUnit?: boolean;
  useCurrencySymbol?: boolean;
  showCurrencyCodeWithSymbol?: boolean;
  positivePrefix?: string;
}): string => {
  const adjustedValue = isMinorUnit ? (value ? value / 100 : 0) : (value ?? 0);
  const currencyCode = currency ?? DEFAULT_CURRENCY;

  const isNegative = adjustedValue < 0;
  const signPrefix = isNegative ? "-" : positivePrefix;

  const absValue = Math.abs(adjustedValue);

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: forceDecimals ? maxFractionDigits : 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(absValue);

  if (!showCurrency || !currencyCode) {
    return `${signPrefix} ${formattedNumber}`;
  }

  if (showCurrencyCodeWithSymbol) {
    const symbol = getCurrencySymbol(currencyCode);

    return `${signPrefix}${symbol}${formattedNumber} ${currencyCode}`;
  }

  const currencyPart = useCurrencySymbol
    ? getCurrencySymbol(currencyCode)
    : currencyCode;

  const parts =
    currencyPosition === "start"
      ? [currencyPart, formattedNumber]
      : [formattedNumber, currencyPart];

  const separator = useCurrencySymbol ? "" : " ";

  return `${signPrefix}${parts.join(separator)}`;
};

export const getFullName = (nameParts?: NameParts | null): string => {
  if (!nameParts) return "";

  const { firstName = null, middleName = null, lastName = null } = nameParts;

  const orderedNameParts = [firstName, middleName, lastName];

  return orderedNameParts
    .map(part => (typeof part === "string" ? part.trim() : ""))
    .filter(part => part.length > 0)
    .join(" ");
};

export const formatAddress = (address?: AddressFormatType | null): string[] => {
  if (!address) return Array(6).fill(NO_DATA);

  const {
    houseNumber,
    addressLine1,
    addressLine2,
    countyOrCity,
    postalCode,
    country,
  } = address;

  const addressLines: string[] = [
    houseNumber ?? NO_DATA,
    addressLine1 ?? NO_DATA,
    addressLine2 ?? NO_DATA,
    countyOrCity ?? NO_DATA,
    postalCode ?? NO_DATA,
    country ?? NO_DATA,
  ];

  return addressLines;
};

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const getCurrencyList = () => {
  const currencyList = [
    {
      currency: "GBP",
    },
    {
      currency: "USD",
    },
    {
      currency: "EUR",
    },
  ];

  return currencyList;
};

export const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

export const copyToClipboard = (text: string, callback?: () => void) => {
  navigator.clipboard.writeText(text);
  callback?.();
};

export const getPreservationMode = (same: boolean): PreservationMode =>
  same ? PRESERVATION_MODE.PRESERVE : PRESERVATION_MODE.NEW;

export const getFormCountries = (allowedCountries = countriesList) => {
  const filteredCountries = defaultCountries.filter(country => {
    const { iso2 } = parseCountry(country);
    return allowedCountries.includes(iso2);
  });

  const selectCountries = filteredCountries.map(country => {
    const countryCode = country[1].toUpperCase();
    return {
      label: country[0],
      value: countryCode,
      search: `${country[0]} ${country[1]} ${country[2]}`,
      iso: countryToCurrency[countryCode as keyof typeof countryToCurrency],
    };
  });

  const phoneInputFlags = filteredCountries.map(country => {
    const { iso2 } = parseCountry(country);
    return {
      iso2,
      src: `/flags/${iso2}.svg`,
    };
  });

  return {
    selectCountries,
    phoneNumberCountries: filteredCountries,
    phoneInputFlags,
  };
};

/**
 *  Remove leading/trailing spaces and reduce multiple spaces between words to a single space
 */
export const trimWhitespace = (str: string) => str.replace(/\s+/g, " ").trim();

export const renderTextOrPlaceholder = ({
  value,
}: {
  value?: string | null;
}) => (value?.trim() ? value : NO_DATA);

export const isValidUuid = (value: string) =>
  /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value.trim());

export const convertToCSV = (data: Record<string, unknown>[]): string => {
  if (data.length === 0) return "";

  const keys = Object.keys(data[0]);
  const csvRows = [
    keys.join(","),
    ...data.map(row =>
      keys.map(key => JSON.stringify(row[key] ?? "")).join(","),
    ),
  ];

  return csvRows.join("\n");
};

/**
 * Extracts a human-readable detail from a GraphQL error.
 *  Supports both problem.detail and extensions.errors formats.
 */
export const extractGraphQLErrorMessage = (
  graphQLError: any,
): string | undefined => {
  const problemDetail = graphQLError?.extensions?.problem?.detail;
  if (problemDetail) return problemDetail;

  const maybeErrors = (
    graphQLError?.extensions as { errors?: GraphQLErrorDetails[] }
  )?.errors;

  return maybeErrors?.[0]?.detail || maybeErrors?.[0]?.message;
};

/**
 * Extracts a human-readable message from a REST error. Handles both
 * RestError instances (Problem Details nested under `data`) and bare
 * RFC 9457 ProblemDetails payloads.
 */
export const extractRestErrorMessage = (error: unknown): string | undefined => {
  if (!error) return;
  const obj = error as {
    data?: { detail?: string; title?: string };
    detail?: string;
    title?: string;
    message?: string;
  };
  return (
    obj.data?.detail ||
    obj.data?.title ||
    obj.detail ||
    obj.title ||
    obj.message
  );
};

export const maxValueValidator = (message: string, max: number): Rule => ({
  validator: (_rule, value) =>
    value && Number(value) > max
      ? Promise.reject(new Error(message))
      : Promise.resolve(),
});

export const generateSlug = (string: string): string => {
  return kebabCase(string);
};

export const downloadFileFromResponse = async (
  response: Response,
  fileName: string,
) => {
  const blob = await response.blob();
  const url = globalThis.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }, 0);
};
